import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { type SetTenantSmtpConnectionInput } from "~/gql/graphqlBase.generated";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import { useSnacks } from "~/providers/SnackProvider";
import {
	useRemoveTenantSmtpConnectionMutation,
	useSetTenantSmtpConnectionMutation,
	useVerifyTenantSmtpConnectionMutation
} from "~/services/endpoints/apiEndpoints";
import { type SmtpTestSendOutcome, smtpTestSendOutcome } from "~admin/email/helpers/ownEmailSystemStatus";

const SMTP_CONNECTION_ENTITY = "SMTP connection";

export interface UseEmailConnectionMutationsReturn {
	isRemoving: boolean;
	isSaving: boolean;
	isVerifying: boolean;
	removeConnection: () => Promise<void>;
	saveConnection: (input: SetTenantSmtpConnectionInput) => Promise<boolean>;
	verifyConnection: (testRecipient: string) => Promise<SmtpTestSendOutcome>;
}

const useEmailConnectionMutations = (): UseEmailConnectionMutationsReturn => {
	const [openSnack] = useSnacks();
	const { t } = useTranslation("common");
	const { run, runForEntity } = useMutationRunner();
	const [setConnectionMutation, { isLoading: isSaving }] = useSetTenantSmtpConnectionMutation();
	const [verifyMutation, { isLoading: isVerifying }] = useVerifyTenantSmtpConnectionMutation();
	const [removeMutation, { isLoading: isRemoving }] = useRemoveTenantSmtpConnectionMutation();

	const saveConnection = useCallback(
		(input: SetTenantSmtpConnectionInput): Promise<boolean> =>
			run(
				setConnectionMutation({ input }),
				(mutationData) => mutationData.setTenantSmtpConnection,
				snackMessages.saveError(SMTP_CONNECTION_ENTITY),
				{ successMessage: snackMessages.saveSuccess(SMTP_CONNECTION_ENTITY) }
			),
		[run, setConnectionMutation]
	);

	const verifyConnection = useCallback(
		async (testRecipient: string): Promise<SmtpTestSendOutcome> => {
			const payload = await runForEntity(
				verifyMutation({ input: { testRecipient } }),
				(mutationData) => mutationData.verifyTenantSmtpConnection,
				(verifyPayload) => verifyPayload,
				t("admin.email.smtp.testUnconfirmedSnack")
			);
			const outcome = smtpTestSendOutcome(payload?.connection?.status);
			if (outcome === "unknown") return outcome;

			const verified = outcome === "verified";
			openSnack(
				verified ? "Test email sent — connection verified" : "Test send failed — see the status below for details",
				verified ? "success" : "warning"
			);
			return outcome;
		},
		[openSnack, runForEntity, t, verifyMutation]
	);

	const removeConnection = useCallback(async (): Promise<void> => {
		await run(
			removeMutation(),
			(mutationData) => mutationData.removeTenantSmtpConnection,
			snackMessages.removeError(SMTP_CONNECTION_ENTITY),
			{ successMessage: snackMessages.removeSuccess(SMTP_CONNECTION_ENTITY) }
		);
	}, [removeMutation, run]);

	return { isRemoving, isSaving, isVerifying, removeConnection, saveConnection, verifyConnection };
};

export default useEmailConnectionMutations;
